import React, { useState, useEffect } from 'react';
import BBALoader from '../components/BBALoader';
import { auth, db, functions } from '../firebase';
import { signInWithEmailAndPassword } from 'firebase/auth';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { doc, setDoc, serverTimestamp } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { addAccount } from '../services/firestoreService';
import { createTask } from '../services/taskService';
import LoginBackground from '../components/LoginBackground';
import BBALogoLockup from '../components/BBALogoLockup';

// Invites are read and redeemed on the server (functions/invites.js): the browser
// can't read invite docs or grant itself staff access / an account link.
const lookupInviteFn = httpsCallable(functions, 'lookupInvite');
const acceptInviteFn = httpsCallable(functions, 'acceptInvite');

const SignupPage = () => {
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    confirmPassword: ''
  });
  
  const [passwordVisible, setPasswordVisible] = useState(false);
  const [confirmPasswordVisible, setConfirmPasswordVisible] = useState(false);
  const [passwordRequirements, setPasswordRequirements] = useState({
    length: false,
    uppercase: false,
    special: false,
    number: false
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [inviteData, setInviteData] = useState(null);
  const [hasInviteOrEmail, setHasInviteOrEmail] = useState(false);
  const [portalInviteData, setPortalInviteData] = useState(null);
  const [validatingInvite, setValidatingInvite] = useState(false);
  const [isInternalUserInvite, setIsInternalUserInvite] = useState(false);
  const [internalInviteData, setInternalInviteData] = useState(null);


  // Create default tasks for new portal users - DISABLED
  // const createDefaultTasks = async (userId, userName) => {
  //   const defaultTasks = [
  //     {
  //       title: 'Complete your profile',
  //       description: 'Update your profile information and preferences to get the most out of your portal experience.',
  //       category: 'onboarding',
  //       status: 'Not Done'
  //     },
  //     {
  //       title: 'Explore your dashboard',
  //       description: 'Take some time to familiarize yourself with your portal dashboard and available features.',
  //       category: 'onboarding',
  //       status: 'Not Done'
  //     }
  //   ];

  //   try {
  //     // Create each default task
  //     for (const taskData of defaultTasks) {
  //       await createTask({
  //         ...taskData,
  //         assignees: [userId],
  //         accountId: 'portal',
  //         completed: false
  //       });
  //     }
  //     console.log('Default tasks created successfully');
  //   } catch (error) {
  //     console.error('Error creating default tasks:', error);
  //     // Don't fail the signup if task creation fails
  //   }
  // };

  // Check for invite on component mount
  useEffect(() => {
    const checkInvite = async () => {
      const inviteId = searchParams.get('invite');
      const inviteEmail = searchParams.get('inviteEmail');
      const token = searchParams.get('token');
      
      if (token) {
        // Handle portal invite token
        setValidatingInvite(true);
        try {
          const { data: portalInvite } = await lookupInviteFn({ token });
          setPortalInviteData(portalInvite);
          setFormData(prev => ({ ...prev, email: portalInvite.email }));
          setHasInviteOrEmail(true);
        } catch (error) {
          console.error('Error validating portal invite:', error);
          setError('Invalid or expired invitation link. Please contact support for a new invitation.');
        } finally {
          setValidatingInvite(false);
        }
      } else if (inviteId) {
        // Handle internal user invite
        setValidatingInvite(true);
        try {
          const { data: inviteData } = await lookupInviteFn({ inviteId });
          setIsInternalUserInvite(true);
          setInternalInviteData(inviteData);
          setFormData(prev => ({ ...prev, email: inviteData.email }));
          setHasInviteOrEmail(true);
        } catch (error) {
          console.error('Error validating internal invite:', error);
          setError(error?.code === 'functions/not-found'
            ? 'This invitation has already been used or is no longer valid.'
            : 'Error validating invitation. Please try again or contact support.');
        } finally {
          setValidatingInvite(false);
        }
      } else if (inviteEmail) {
        // Handle legacy portal user invite with just email
        setHasInviteOrEmail(true);
        setInviteData({
          email: inviteEmail,
          role: 'client', // Portal users are clients
          team: 'Portal'
        });
      } else {
        // Portal signup via invite link
        setHasInviteOrEmail(true);
        setInviteData(null);
      }
    };
    
    checkInvite();
  }, [searchParams, navigate]);

  // Password validation
  useEffect(() => {
    const { password } = formData;
    setPasswordRequirements({
      length: password.length >= 8,
      uppercase: /[A-Z]/.test(password),
      special: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      number: /\d/.test(password)
    });
  }, [formData.password]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Check if passwords match
    if (formData.password !== formData.confirmPassword) {
      setError('Passwords do not match. Please try again.');
      return;
    }
    
    // Check if password meets all requirements
    const allRequirementsMet = Object.values(passwordRequirements).every(requirement => requirement);
    if (!allRequirementsMet) {
      setError('Please ensure your password meets all requirements.');
      return;
    }
    
    setLoading(true);
    try {
      // Logins are created by the server when an invite is redeemed (public sign-up is off)
      if (!(isInternalUserInvite && internalInviteData) && !portalInviteData) {
        setError('Signing up requires an invitation link. Please contact your administrator or BBA representative.');
        return;
      }

      // Get device timezone
      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      if (isInternalUserInvite && internalInviteData) {
        // The server creates the login, staff profile and role claim from the stored invite
        const { data: accepted } = await acceptInviteFn({ inviteId: searchParams.get('invite'), password: formData.password });
        await signInWithEmailAndPassword(auth, accepted.email, formData.password);

        // Navigate to CRM dashboard for internal users
        navigate('/crm', { 
          state: { 
            message: 'Account created successfully! Welcome to the CRM system.',
            userType: 'internal'
          }
        });
        return;
      } else {
        // Portal user. Redeeming the invite on the server links this login to the
        // lead/client account and moves its stage; it returns the account details.
        const { data: accepted } = await acceptInviteFn({ token: searchParams.get('token'), password: formData.password });
        const { user } = await signInWithEmailAndPassword(auth, accepted.email, formData.password);
        const currentAccountData = { company: accepted.company, contact: accepted.contact, type: accepted.accountType };

        // Helper function to get valid name values
        const getValidName = (value) => {
          if (!value || value === 'undefined' || value === 'null') return '';
          return String(value).trim();
        };

        // Get firstName: prioritize portal invite data (invite was created for this specific person - team members get their own name)
        // then account contact, then email prefix
        const firstName = getValidName(portalInviteData?.firstName) || 
                         getValidName(currentAccountData.contact?.firstName) || 
                         formData.email.split('@')[0] || '';

        // Get lastName: prioritize portal invite data, then account contact
        const lastName = getValidName(portalInviteData?.lastName) || 
                        getValidName(currentAccountData.contact?.lastName) || '';

        // Get company: prioritize account company, then portal invite data, then default
        const companyValue = getValidName(currentAccountData.company) || 
                            getValidName(portalInviteData?.company) || 
                            'Unknown Company';

        // Create portal user document in Firestore
        const userDocRef = doc(db, 'users', user.uid);
        const userData = {
          uid: user.uid,
          email: user.email,
          firstName,
          lastName,
          role: 'client', // Portal users are clients
          status: 'onboarding', // Start in onboarding mode
          timezone,
          createdAt: serverTimestamp(),
          lastLogin: serverTimestamp(),
          permissions: ['view:own', 'edit:own', 'read:own'], // Basic permissions for portal users
          receiveEmails: true, // Default to true for simplified signup
          company: companyValue,
          onboardingStatus: {
            completed: false,
            currentStep: 'existing-conditions',
            steps: {
              'existing-conditions': {
                completed: false,
                required: true
              },
              'nda': {
                completed: false,
                required: true
              },
              'profile-completion': {
                completed: false,
                required: true
              }
            }
          },
          // Track if this was an invited user (portal is invite-only)
          invitedBy: 'portal-invite',
          inviteData: inviteData || null,
          // Add user type for access control
          userType: 'portal'
        };

        // merge: acceptInvite already wrote accountId/clientId on this doc
        await setDoc(userDocRef, userData, { merge: true });

        try {
          const { triggerPortalSignupNotification } = await import('../services/notificationTriggerService');
          const fullName = `${firstName} ${lastName}`.trim() || formData.email.split('@')[0] || 'Unknown User';
          await triggerPortalSignupNotification({
            email: formData.email,
            name: fullName,
            signupDate: new Date().toISOString(),
            status: 'active',
            company: companyValue,
            leadId: accepted.leadId,
            timestamp: new Date().toISOString()
          });
        } catch (notificationError) {
          console.error('❌ Error sending portal signup notification:', notificationError);
          // Don't fail the signup if notification fails
        }

        // Clients land on the portal dashboard, leads on the start flow
        const redirectPath = accepted?.redirect || '/start';
        const welcomeMessage = redirectPath === '/portal'
          ? 'Account created successfully! Welcome to your client portal.'
          : 'Account created successfully! Welcome to your portal.';
        
        // Navigate to the appropriate dashboard
        navigate(redirectPath, { 
          state: { 
            message: welcomeMessage
          }
        });
      }
    } catch (err) {
      console.error('Signup error:', err);
      
      // Provide user-friendly error messages
      let userFriendlyMessage = 'An error occurred during signup. Please try again.';
      
      if (err.code === 'auth/email-already-in-use') {
        userFriendlyMessage = 'This email address is already in use. Please use a different email or try logging in instead.';
      } else if (err.code === 'auth/weak-password') {
        userFriendlyMessage = 'Password is too weak. Please choose a stronger password.';
      } else if (err.code === 'auth/invalid-email') {
        userFriendlyMessage = 'Please enter a valid email address.';
      } else if (err.code === 'auth/operation-not-allowed') {
        userFriendlyMessage = 'Email/password accounts are not enabled. Please contact support.';
      } else if (err.code === 'auth/too-many-requests') {
        userFriendlyMessage = 'Too many failed attempts. Please try again later.';
      } else if (err.message) {
        // For other Firebase errors, use the message but remove technical prefixes
        userFriendlyMessage = err.message.replace(/^Firebase:\s*Error\s*\([^)]+\)\.?\s*/i, '');
      }
      
      setError(userFriendlyMessage);
    } finally {
      setLoading(false);
    }
  };

  const togglePasswordVisibility = () => {
    setPasswordVisible(!passwordVisible);
  };

  const toggleConfirmPasswordVisibility = () => {
    setConfirmPasswordVisible(!confirmPasswordVisible);
  };

  return (
    <div className="min-h-screen relative flex items-center justify-center p-4">
      <LoginBackground />

      {/* Signup Content */}
      <div className="w-full max-w-md relative z-10">
        {/* Signup Card */}
        <div className="bg-white rounded-2xl shadow-xl p-8">
          {/* Logo and Title Section */}
          <div className="text-center mb-8">
            <BBALogoLockup className="mb-6" />
            <h1 className="text-3xl font-bold text-gray-900 mb-2">
              {isInternalUserInvite ? 'Complete your account setup' : 'Create your account'}
            </h1>
            <p className="text-gray-600">
              {isInternalUserInvite 
                ? 'Set up your password to access the CRM system' 
                : 'Join our portal to access your business resources and tools'
              }
            </p>
            {isInternalUserInvite && internalInviteData && (
              <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                <p className="text-sm text-blue-700">
                  <span className="font-medium">Welcome {internalInviteData.firstName}!</span><br />
                  You've been invited as a <strong>{internalInviteData.role}</strong> on the <strong>{internalInviteData.team}</strong> team.
                </p>
              </div>
            )}
          </div>
          
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg">
              <div className="flex">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3">
                  <p className="text-sm text-red-700">{error}</p>
                </div>
              </div>
            </div>
          )}
          
          {validatingInvite ? (
            <div className="space-y-6">
              <div className="text-center">
                <BBALoader size={48} className="mx-auto mb-4" />
                <p className="text-gray-600">Validating your invitation...</p>
              </div>
            </div>
          ) : hasInviteOrEmail ? (
            <form onSubmit={handleSubmit} className="space-y-6">
              <div>
                <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                  Email address
                </label>
                <input
                  type="email"
                  id="email"
                  name="email"
                  value={formData.email}
                  onChange={handleChange}
                  disabled={!!portalInviteData || isInternalUserInvite}
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200 disabled:bg-gray-100 disabled:cursor-not-allowed"
                  placeholder="Enter your email"
                  required
                />
              </div>

              <div>
                <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                  Password
                </label>
                <div className="relative">
                  <input
                    type={passwordVisible ? "text" : "password"}
                    id="password"
                    name="password"
                    value={formData.password}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                    placeholder="Create a password"
                    required
                  />
                  <button
                    type="button"
                    onClick={togglePasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                  >
                    {passwordVisible ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
                
                {/* Password Requirements */}
                <div className="mt-2 space-y-1">
                  <div className={`text-xs ${passwordRequirements.length ? 'text-green-600' : 'text-gray-500'}`}>
                    ✓ At least 8 characters
                  </div>
                  <div className={`text-xs ${passwordRequirements.uppercase ? 'text-green-600' : 'text-gray-500'}`}>
                    ✓ At least one uppercase letter
                  </div>
                  <div className={`text-xs ${passwordRequirements.special ? 'text-green-600' : 'text-gray-500'}`}>
                    ✓ At least one special character
                  </div>
                  <div className={`text-xs ${passwordRequirements.number ? 'text-green-600' : 'text-gray-500'}`}>
                    ✓ At least one number
                  </div>
                </div>
              </div>

              <div>
                <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password
                </label>
                <div className="relative">
                  <input
                    type={confirmPasswordVisible ? "text" : "password"}
                    id="confirmPassword"
                    name="confirmPassword"
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    className="w-full px-4 py-3 rounded-lg border border-gray-300 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition duration-200"
                    placeholder="Confirm your password"
                    required
                  />
                  <button
                    type="button"
                    onClick={toggleConfirmPasswordVisibility}
                    className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-500"
                  >
                    {confirmPasswordVisible ? (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13.875 18.825A10.05 10.05 0 0112 19c-4.478 0-8.268-2.943-9.543-7a9.97 9.97 0 011.563-3.029m5.858.908a3 3 0 114.243 4.243M9.878 9.878l4.242 4.242M9.878 9.878L3 3m6.878 6.878L21 21" />
                      </svg>
                    ) : (
                      <svg className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>

              <button
                type="submit"
                disabled={loading}
                className="w-full py-3 px-4 bg-red-600 hover:bg-red-700 text-white font-medium rounded-lg transition duration-200 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-offset-2"
              >
                {loading ? (
                  <div className="flex items-center justify-center">
                    <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    {isInternalUserInvite ? 'Setting up account...' : 'Creating account...'}
                  </div>
                ) : (
                  isInternalUserInvite ? 'Complete Setup' : 'Create account'
                )}
              </button>
            </form>
          ) : (
            <div className="space-y-6">
              <div className="p-4 bg-blue-50 border border-blue-200 rounded-lg">
                <div className="flex">
                  <div className="flex-shrink-0">
                    <svg className="h-5 w-5 text-blue-400" viewBox="0 0 20 20" fill="currentColor">
                      <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
                    </svg>
                  </div>
                  <div className="ml-3">
                    <p className="text-sm text-blue-700">
                      <span className="font-medium">Welcome!</span> Create your portal account to get started with our business resources and tools.
                    </p>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SignupPage; 